import { useCallback, useRef, useState } from 'react'
import {
  SprintReport,
  getSprintReport,
} from '../../../../lib/functions/sprintReport'
import { SprintReportRow } from '../model/sprintReportRow'
import {
  OrganizationWorkingDayCalendarDetail,
  getOrganizationWorkingDayCalendar,
} from '../../../../lib/functions/organizationWorkingDayCalendar'
import { SprintReportSearchCondition } from './searchCondition'
import { dateV2Service } from '../../../../domain/value-object/DateV2'

export type SprintReportDataApi = {
  rowData: SprintReportRow[]
  workingDayCalendars: OrganizationWorkingDayCalendarDetail[]
  loading: boolean
  // 最後に成功したフェッチ時の rootWbsItemUuid。パンくず表示など「実際に読み込まれた WBS」に追従させたい UI で参照する。
  // 検索条件 (searchCondition.rootWbsItemUuid) は WbsItemSelect の入力中に一時的に undefined にクリアされうるため、
  // 表示の安定化には fetch 成功時に確定したこちらを使う。
  appliedRootWbsItemUuid: string | undefined
  refresh: (
    projectUuid: string,
    searchCondition: SprintReportSearchCondition
  ) => Promise<void>
}

const toApiDateString = (timestamp: number | undefined): string | undefined => {
  const date = dateV2Service.fromNumber(timestamp)
  return date ? dateV2Service.fromVoToApiRequest(date) : undefined
}

const toDate = (timestamp: number | undefined): Date | undefined =>
  dateV2Service.fromNumber(timestamp)

// BulkSheetView の getRowId は data.uuid を参照するが、API レスポンスには uuid がないため
// (member.uuid, key) で安定な行IDを生成する。同じ ID が複数行に返ると ag-grid が行を取りこぼし先頭行が消える。
const toRow = (response: SprintReport): SprintReportRow => ({
  ...response,
  uuid: `${response.member.uuid}-${response.key}`,
})

export const useSprintReportData = (): SprintReportDataApi => {
  const [rowData, setRowData] = useState<SprintReportRow[]>([])
  const [workingDayCalendars, setWorkingDayCalendars] = useState<
    OrganizationWorkingDayCalendarDetail[]
  >([])
  const [loading, setLoading] = useState<boolean>(false)
  const [appliedRootWbsItemUuid, setAppliedRootWbsItemUuid] = useState<
    string | undefined
  >(undefined)
  // パンくず連打や検索ボタン連打で複数の refresh が並走したとき、遅いレスポンスが速いレスポンスを
  // 上書きして古いデータを表示してしまう race を避けるため、リクエストごとに ID を振って
  // 最新でないレスポンスは破棄する。
  const requestIdRef = useRef(0)

  const refresh = useCallback(
    async (
      projectUuid: string,
      searchCondition: SprintReportSearchCondition
    ) => {
      const requestId = ++requestIdRef.current
      const { dateTerm } = searchCondition
      if (!dateTerm.startDate || !dateTerm.endDate) {
        setRowData([])
        setWorkingDayCalendars([])
        setAppliedRootWbsItemUuid(searchCondition.rootWbsItemUuid)
        return
      }
      setLoading(true)
      try {
        const [reportResponse, calendarResponse] = await Promise.all([
          getSprintReport({
            projectUuid,
            rootWbsItemUuid: searchCondition.rootWbsItemUuid,
            teamUuid: searchCondition.teamUuid,
            groupKey: searchCondition.groupKey,
            startDate: toApiDateString(dateTerm.startDate),
            endDate: toApiDateString(dateTerm.endDate),
          }),
          getOrganizationWorkingDayCalendar({
            startDate: toDate(dateTerm.startDate),
            endDate: toDate(dateTerm.endDate),
          }),
        ])
        if (requestId !== requestIdRef.current) return
        setRowData((reportResponse.json ?? []).map(toRow))
        setWorkingDayCalendars(calendarResponse.json ?? [])
        setAppliedRootWbsItemUuid(searchCondition.rootWbsItemUuid)
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    []
  )

  return {
    rowData,
    workingDayCalendars,
    loading,
    appliedRootWbsItemUuid,
    refresh,
  }
}
